import { useQuery } from "@tanstack/react-query";
import { fetchAllPaginated } from "@/lib/supabase-paginate";

/**
 * Loggede timer per prosjekt for én kunde (Map<projectId, timer>). Brukes av
 * kundekortets nøkkeltall («Snitt timepris reell»). Bundet på kundens
 * prosjekt-id-er, men går likevel via fetchAllPaginated: time_entries
 * passerer lett 1000 rader for en stor kunde (paginate-gate).
 */
export function useCustomerHours(customerId: string | undefined, projectIds: readonly string[]) {
  const ids = [...projectIds].sort();
  return useQuery({
    queryKey: ["customer-hours", customerId, ids],
    queryFn: async (): Promise<Map<string, number>> => {
      const map = new Map<string, number>();
      if (ids.length === 0) return map;
      const rows = await fetchAllPaginated<{ project_id: string | null; duration_seconds: number | null }>(
        "time_entries",
        "project_id, duration_seconds",
        (q) => q.in("project_id", ids).is("deleted_at", null),
      );
      for (const r of rows) {
        if (!r.project_id) continue;
        map.set(r.project_id, (map.get(r.project_id) ?? 0) + (r.duration_seconds ?? 0) / 3600);
      }
      return map;
    },
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Solgte timer per prosjekt for én kunde (Map<projectId, timer>): sum
 * quantity_hours på interne budsjettlinjer, samme grunnlag som
 * budsjettavviket i prosjektlista (useProjectHours).
 */
export function useCustomerBudgetedHours(customerId: string | undefined, projectIds: readonly string[]) {
  const ids = [...projectIds].sort();
  return useQuery({
    queryKey: ["customer-budgeted-hours", customerId, ids],
    queryFn: async (): Promise<Map<string, number>> => {
      const map = new Map<string, number>();
      if (ids.length === 0) return map;
      const rows = await fetchAllPaginated<{ project_id: string | null; quantity_hours: number | null }>(
        "budget_lines",
        "project_id, quantity_hours",
        (q) => q.in("project_id", ids).eq("cost_type", "internal"),
      );
      for (const r of rows) {
        if (!r.project_id) continue;
        map.set(r.project_id, (map.get(r.project_id) ?? 0) + Number(r.quantity_hours ?? 0));
      }
      return map;
    },
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
  });
}
